import React, { memo, useEffect, useState, useRef } from "react";
import classes from "./PlannerOverviewV2.scss";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import I18nHOC from "UIComponents/I18nHOC";
import {
  updateTemplate,
  FILTER_REQUIRED_PLANNER_ELEMENT_SETS,
  shouldConvertNonPortedFieldsToPorted,
} from "IBPlanner/modules/IBPlannerModule";
import { getUnitSummaryPlanQuery } from "IBPlanner/modules/IBQuery";
import { getUnitSummaryPlanDetailsFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import {
  generateFieldListFromTemplateFields,
  getUnitPlanStagesMemoized,
  getPlannerElementNodeFiltersMemoized,
} from "UnitPlans/modules/Utils";
import {
  getTemplateFieldQueryEnhancer,
  getPlannerElementNodeSetsFromIdsVariables,
  getPlannerElementNodeSetIdsOfCurriculumVariables,
} from "UnitPlans/modules/UnitPlanQueryEnhancers";
import {
  getSelectedAcademicYear,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE } from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import Accordion from "./components/Accordion";
import RightPaneHeader from "UnitPlans/routes/UnitPlanDetails/components/RightPane/RightPaneHeader/RightPaneHeader";
import PlannerOverviewSkeletonLoader from "./components/PlannerOverviewSkeletonLoader";
import _ from "lodash";
import { getUnitFlowUtils } from "UnitFlowV3/modules/utils";

import { createSelector } from "reselect";
import {
  getPlannerElementNodeSetsFromIdsFromCache,
  getPlannerElementNodeSetIdsOfCurriculumFromCache,
} from "UnitPlans/modules/UnitPlanGraphqlHelpers";
import {
  getFilteredPlannerElementNodeSets,
  getMergedPlannerElementNodeSets,
  getAllPlannerElementTypesFromFields,
  VISIBLE_ELEMENT_TYPES_FOR_V2,
  getBaseCurriculumProgram,
} from "UnitPlans/modules/UnitPlanModule/UnitPlanUtils";

const getStructuredData = (
  stages,
  stage_list,
  step_list,
  field_list,
  fieldListV2
) => {
  return stages
    .map(stageId => {
      const stage = stage_list[stageId];
      if (!stage) return null;

      const steps = stage.steps
        .map(stepId => {
          const step = step_list[stepId];
          if (!step) return null;

          const fields = step.fields
            .map(fieldId => {
              const field = field_list[fieldId] || fieldListV2[fieldId];
              return field ? { ...field, id: fieldId } : null;
            })
            .filter(Boolean);

          return { id: stepId, ...step, fields };
        })
        .filter(Boolean);

      return { id: stageId, ...stage, steps };
    })
    .filter(Boolean);
};

const PlannerOverviewV2 = memo(function PlannerOverviewV2(props) {
  const {
    unitPlanFields,
    onClose,
    unitPlanTemplate,
    t,
    unitPlanDetails,
    selectedAcademicYear,
    isUnitFlowRevamp,
    testId,
    plannerElementNodeSets,
    grades,
    isLoading,
    isData,
  } = props;

  const [hasScrollbar, setHasScrollbar] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const checkForScrollbar = () => {
      if (scrollContainerRef.current) {
        const { clientHeight, scrollHeight } = scrollContainerRef.current;
        setHasScrollbar(scrollHeight > clientHeight);
      }
    };

    checkForScrollbar();
    // Re-check when content changes
    const resizeObserver = new ResizeObserver(checkForScrollbar);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [isData, isLoading]);

  const unitSummaryModules = _.get(
    unitPlanTemplate,
    "unitSummaryModules",
    null
  );

  const updatedStageModuleLists = getUnitPlanStagesMemoized({
    unitPlanDetails,
    unitSummaryModules,
    module_list: _.get(unitPlanTemplate, "module_list", {}),
    unitPlanFields,
    stage_list: _.get(unitPlanTemplate, "stage_list", {}),
    step_list: _.get(unitPlanTemplate, "step_list", {}),
    field_list: _.get(unitPlanTemplate, "field_list", {}),
    fieldListV2: _.get(unitPlanTemplate, "fieldListV2", {}),
    removeFirstStep: true, // Flag to remove the first step of the first stage
  });

  const updatedStageList = updatedStageModuleLists.updatedStageList;

  const showSkeletonLoader = isLoading || !isData;

  return (
    <div className={classes.container}>
      <RightPaneHeader
        title={t("common:planner_overview")}
        onClose={onClose}
        testId={`${testId}-plannerOverview-header`}
      />
      <div className={classes.scrollContainer} ref={scrollContainerRef}>
        {showSkeletonLoader ? (
          <PlannerOverviewSkeletonLoader />
        ) : (
          <Accordion
            t={t}
            unitSummaryModules={unitSummaryModules}
            unitPlanFields={unitPlanFields}
            module_list={_.get(unitPlanTemplate, "module_list", {})}
            stage_list={_.get(unitPlanTemplate, "stage_list", {})}
            step_list={_.get(unitPlanTemplate, "step_list", {})}
            field_list={_.get(unitPlanTemplate, "field_list", {})}
            fieldListV2={_.get(unitPlanTemplate, "fieldListV2", {})}
            hasScrollbar={hasScrollbar}
            unitPlanDetails={unitPlanDetails}
            selectedAcademicYear={selectedAcademicYear}
            isUnitFlowRevamp={isUnitFlowRevamp}
            itemList={updatedStageList}
            testId={`${testId}-plannerOverview-header`}
            overrideClasses={classes}
            plannerElementNodeSets={plannerElementNodeSets}
            grades={grades}
          />
        )}
      </div>
    </div>
  );
});

const updateTemplateMemoize = _.memoize(
  ({
    template,
    unitType,
    curriculumType,
    resolvedSubjectNodes,
    t,
    fieldListV2,
    isSinglePageAndNewUnitView,
    plannerElementSets,
  }) =>
    updateTemplate({
      t,
      template,
      unitType,
      curriculumType,
      resolvedSubjectNodes,
      fieldListV2,
      isSinglePageAndNewUnitView,
      plannerElementSets,
    }),
  params => JSON.stringify(params)
);

const mapActionCreators = {};

const plannerElementSetsItems = state =>
  state?.platform?.currentPlannerElementSets;

const plannerElementSetsSelector = createSelector(
  plannerElementSetsItems,
  plannerElementSets => _.map(plannerElementSets, item => item)
);

const mapStateToProps = state => {
  const selectedAcademicYear = getSelectedAcademicYear(state);
  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );
  const curriculumProgramId = getSelectedCurriculumId(state);
  const { isUnitFlowRevampEnabled } = getUnitFlowUtils();
  const isUnitFlowRevamp = isUnitFlowRevampEnabled();
  const academicYearId = _.get(selectedAcademicYear, "id", "");
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  const plannerElementSets = plannerElementSetsSelector(state);

  const baseCurriculum = getBaseCurriculumProgram(state, {
    currentCurriculumProgramId: curriculumProgramId,
    currentCurriculumProgramType,
  });

  return {
    isData: true,
    isLoading: false,
    isUnitFlowRevamp,
    organizationId,
    selectedAcademicYear,
    currentCurriculumProgramType,
    curriculumProgramId,
    curriculumType: currentCurriculumProgramType,
    curriculumId: curriculumProgramId,
    academicYearId,
    // These props will be populated later based on the unit plan data
    plannerElementTypes: [],
    grades: [],
    isPypPlannerTheme: false,
    plannerElementSets,
    baseCurriculumId: _.get(baseCurriculum, "id", ""),
    baseCurriculumNodeInterfaceType: _.get(
      baseCurriculum,
      "nodeInterfaceType",
      ""
    ),
  };
};

const templateFieldQueryCallback = ({ data, ownProps }) => {
  const {
    t,
    unitPlanDetails,
    templateId,
    curriculumType,
    academicYearId: currentAcademicYearId,
    plannerElementSets,
    baseCurriculumId,
    baseCurriculumNodeInterfaceType,
  } = ownProps;

  const templateFromEnhancer = _.find(data, { id: templateId });
  const templateFields = _.get(templateFromEnhancer, [
    "templateField",
    "edges",
  ]);

  const unitPlanFields = _.get(unitPlanDetails, "allFields", "");

  const fieldList = generateFieldListFromTemplateFields({
    templateFields,
    templateId,
  });

  const template = _.get(unitPlanDetails, "template", {});

  const resolvedSubjectNodes = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects",
    []
  );

  const unitType = _.get(unitPlanDetails, "unitType.value", "");

  const unitPlanTemplate = _.get(
    updateTemplateMemoize({
      t,
      template,
      unitType,
      curriculumType,
      resolvedSubjectNodes,
      fieldListV2: fieldList,
      isSinglePageAndNewUnitView: true,
      plannerElementSets,
    }),
    "body",
    {}
  );

  const { field_list, fieldListV2: field_listV2 } = unitPlanTemplate;

  const shouldConvertNonPortedFieldsToPortedFlag = shouldConvertNonPortedFieldsToPorted();
  const unitPlanFieldList = shouldConvertNonPortedFieldsToPortedFlag
    ? field_listV2
    : field_list;

  const templateBody = _.get(unitPlanDetails, "template.body", {});

  // Extract planner element types from the stages and steps
  const initialModule = _.get(templateBody, "modules", [])[0];
  const stages = _.get(templateBody, `module_list.${initialModule}.stages`, []);
  const structuredData = getStructuredData(
    stages,
    templateBody.stage_list,
    templateBody.step_list,
    templateBody.field_list,
    fieldList
  );

  const elementTypesSet = new Set();
  const v2UiTypesSet = new Set();

  _.forEach(structuredData, section => {
    _.forEach(section.steps, step => {
      _.forEach(step.fields, field => {
        const fieldId = field.id;
        if (fieldId && _.has(field_listV2, fieldId)) {
          const uiType = _.get(field_listV2[fieldId], "uiType.value");
          if (VISIBLE_ELEMENT_TYPES_FOR_V2.includes(uiType)) {
            v2UiTypesSet.add(uiType);
          }
        }
      });

      _.forEach(step.config?.elementTypes, type => {
        elementTypesSet.add(type);
      });
    });
  });

  // Remove this logics when we introduce plannerElementNodeSet in v2 fields
  const visibleElementTypesForV2 = _.toArray(v2UiTypesSet);

  const extractedPlannerElementTypes = _.union(
    _.toArray(elementTypesSet),
    visibleElementTypesForV2
  );

  const allPlannerElementTypes = getAllPlannerElementTypesFromFields({
    fieldList: unitPlanFieldList,
  });

  // Get curriculumNodeInterfaceType and curriculumId from unitPlanDetails
  const curriculumProgram = unitPlanDetails.curriculumProgram || {};
  const curriculumNodeInterfaceType = _.get(
    curriculumProgram,
    "nodeInterfaceType",
    ""
  );
  const curriculumProgramId = _.get(curriculumProgram, "id", "");

  const unitPlanAcademicYearsIds = _.get(unitPlanDetails, "academicYears", []);
  const academicYearId = _.get(
    unitPlanAcademicYearsIds[0],
    "id",
    currentAcademicYearId
  );

  // Get planner element node set IDs from cache
  const curriculumPlannerElementNodeSets = getPlannerElementNodeSetIdsOfCurriculumFromCache(
    getPlannerElementNodeSetIdsOfCurriculumVariables({
      curriculumId: curriculumProgramId,
      curriculumNodeInterfaceType,
      allPlannerElementTypes,
    })
  );

  const baseCurriculumPlannerElementNodeSets = baseCurriculumId
    ? getPlannerElementNodeSetIdsOfCurriculumFromCache(
        getPlannerElementNodeSetIdsOfCurriculumVariables({
          curriculumId: baseCurriculumId,
          curriculumNodeInterfaceType: baseCurriculumNodeInterfaceType,
          allPlannerElementTypes,
        })
      )
    : [];

  const plannerElementNodeSets = getMergedPlannerElementNodeSets(
    curriculumPlannerElementNodeSets,
    baseCurriculumPlannerElementNodeSets
  );

  // Split into subject-independent and subject-dependent types
  const subjectDependentElementTypes = _.intersection(
    extractedPlannerElementTypes,
    FILTER_REQUIRED_PLANNER_ELEMENT_SETS
  );

  const additionalTypesToRemove = _.concat(
    _.difference(allPlannerElementTypes, extractedPlannerElementTypes),
    subjectDependentElementTypes
  );

  // Get subject-independent planner element node sets
  const filteredPlannerElementNodeSets = getFilteredPlannerElementNodeSets({
    plannerElementNodeSets,
    additionalTypesToRemove,
  });
  const subjectIndependentPlannerElementNodeSets = getPlannerElementNodeSetsFromIdsFromCache(
    getPlannerElementNodeSetsFromIdsVariables({
      plannerElementNodeSets: filteredPlannerElementNodeSets,
      academicYearId,
    })
  );

  // Get subject-dependent planner element node sets with filters
  // Extract subjects and selectedSubjectLevels from the unit plan fields
  const subjectsField = _.find(unitPlanFields, { uid: "subjects" });

  const subjects = {
    value: _.map(
      _.get(subjectsField, "resolvedMinimalTree.subjects", []),
      "id"
    ),
  };

  const subjectLevelsField = _.find(unitPlanFields, {
    uid: "subjectLevel",
  });
  const selectedSubjectLevels = _.get(subjectLevelsField, "value", []);

  const filteredSubjectDependentNodeSets = _.filter(
    getFilteredPlannerElementNodeSets({
      plannerElementNodeSets,
    }),
    nodeSet => {
      return _.includes(subjectDependentElementTypes, nodeSet.type);
    }
  );

  const subjectDependentPlannerElementNodeFilters = getPlannerElementNodeFiltersMemoized(
    {
      selectedSubjectLevels,
      subjects,
      plannerElementTypes: subjectDependentElementTypes,
    }
  );

  const subjectDependentPlannerElementNodeSets = getPlannerElementNodeSetsFromIdsFromCache(
    getPlannerElementNodeSetsFromIdsVariables({
      plannerElementNodeSets: filteredSubjectDependentNodeSets,
      plannerElementNodeFilters: subjectDependentPlannerElementNodeFilters,
    })
  );

  // Merge all planner element node sets with deep merge to preserve fragments
  // First merge: plannerElementNodeSets + subjectIndependentPlannerElementNodeSets
  const mergedWithSubjectIndependent = getMergedPlannerElementNodeSets(
    plannerElementNodeSets,
    subjectIndependentPlannerElementNodeSets
  );

  // Second merge: result + subjectDependentPlannerElementNodeSets
  const allPlannerElementNodeSets = getMergedPlannerElementNodeSets(
    mergedWithSubjectIndependent,
    subjectDependentPlannerElementNodeSets
  );

  return {
    unitPlanTemplate,
    isStepWithPlannerElement: extractedPlannerElementTypes.length > 0,
    isDPLE: false,
    plannerElementNodeSets: allPlannerElementNodeSets,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({
    resource: ["unitPlanTemplate", "unitPlan", "common", "projectTemplate"],
  }),
  graphql(getUnitSummaryPlanQuery, {
    name: "getUnitSummaryPlan",
    options: ({ unitPlanId, i18n }) => {
      const variables = { id: unitPlanId };

      if (ACLStore.can(ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE))
        variables.locale = i18n.language;
      return {
        fetchPolicy: "cache-and-network",
        variables,
      };
    },
    props({ getUnitSummaryPlan, ownProps: { isData, isLoading } }) {
      const unitPlanDetails = getUnitSummaryPlanDetailsFromCache(
        getUnitSummaryPlan.variables
      );

      const unitPlanFields = _.get(unitPlanDetails, "allFields", "");
      const templateId = _.get(unitPlanDetails, ["template", "id"], null);
      const grades = _.get(unitPlanDetails, "grades.value", []);

      return {
        templateId,
        templateIds: [templateId],
        unitPlanDetails,
        unitPlanFields,
        grades,
        isData: !_.isEmpty(unitPlanDetails) && isData,
        isLoading:
          getUnitSummaryPlan["networkStatus"] == 1 ||
          getUnitSummaryPlan["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  getTemplateFieldQueryEnhancer({ callback: templateFieldQueryCallback })
)(PlannerOverviewV2);

export { PlannerOverviewV2 };
